import { LitElement, html } from 'lit';
import { customElement, state, property } from 'lit/decorators.js';
import { GoonAPIClient } from '../client/ApiClient';
import { API_ENDPOINT } from '../constants';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';

const api = new GoonAPIClient(API_ENDPOINT);

const MAX_VIDEO_MB = 100;
const MAX_IMAGE_MB = 10;

@customElement('create-post-modal')
export class CreatePostModal extends LitElement {
  @state() title = '';
  @state() body = '';
  @state() link = '';
  @state() files: File[] = [];
  @state() tab = 'media';
  @state() loading = false;
  @state() error = '';
  @state() linkPreview: any = null;

  @property() channelSlug = '';
  @property() channelId = '';
  @property() open = false;

  createRenderRoot() { return this; }

  handleFiles(e: Event) {
    const target = e.target as HTMLInputElement;
    if (!target.files) return;

    const newFiles: File[] = [];
    const errors: string[] = [];

    for (const file of Array.from(target.files)) {
      const isVideo = file.type.startsWith('video/');
      const isImage = file.type.startsWith('image/');

      if (isVideo) {
        errors.push('Video uploads not yet supported, but coming soon!');
      } else {
        const maxSizeMB = isVideo ? MAX_VIDEO_MB : isImage ? MAX_IMAGE_MB : 0;
        const tooBig = file.size > maxSizeMB * 1024 * 1024;

        if (tooBig) {
          errors.push(`${file.name} is too big. Upgrade to <a class="link link-primary underline" href="/pro">Gooner Pro</a> to unlock larger uploads, unlimited Autogoon usage, multiple Goonsonas, Full Access to AI Tools, Custom Nameplate and more.`);
        } else {
          newFiles.push(file);
        }
      }
    }

    if (errors.length) {
      this.error = errors.join('<br/>');
    } else {
      this.error = '';
    }

    this.files = newFiles;
  }


  async handleSubmit() {
    this.loading = true;
    this.error = '';
    try {
      const mediaUrls: string[] = [];
      for (const file of this.files) {
        const mediaUrl = await api.uploadMediaAndWait(file, 'post-content', file.type);
        mediaUrls.push(mediaUrl);
      }

      const body = this.tab === 'link' ? this.link : this.body;

      await api.createPost({
        title: this.title,
        body,
        mediaUrls,
        channelId: this.channelId,
      });
      window.location.reload();
    } catch (err) {
      console.error(err);
      this.error = 'Failed to create post';
    } finally {
      this.loading = false;
    }
  }

  async handlePasteBody(e: ClipboardEvent) {
    const pasted = e.clipboardData?.getData('text/plain') ?? '';
    if (!this.body && pasted.startsWith('http')) {
      this.tab = 'link';
      this.link = pasted;
      try {
        const title = await api.fetchPageTitle(pasted);
        if (!this.title) this.title = title;
      } catch (e) {
        console.warn('Failed to fetch preview', e);
      }
    }
  }

  async handleLinkInput(e: Event) {
    this.link = (e.target as HTMLInputElement).value;
  }

  close() {
    this.dispatchEvent(new CustomEvent('close'));
  }

  renderPreview() {
    return html`
      <link-preview .url=${this.link}></link-preview>
    `
  }

  render() {
    return html`
      <div class="${this.open ? 'fixed inset-0 z-40 backdrop-blur-sm bg-black/30' : 'hidden'}"></div>
      <dialog class="modal ${this.open ? 'modal-open' : ''}">
        <div class="modal-box w-full max-w-2xl">
          <form method="dialog">
            <button class="btn btn-sm btn-circle btn-ghost absolute right-2 top-2" @click=${() => this.close()}>✕</button>
          </form>
          <h3 class="text-lg font-bold mb-4">Create Post in #${this.channelSlug}</h3>

          <div role="tablist" class="tabs tabs-boxed mb-4">
            <a class="tab ${this.tab === 'media' ? 'tab-active' : ''}" @click=${() => this.tab = 'media'}>Media</a>
            <a class="tab ${this.tab === 'link' ? 'tab-active' : ''}" @click=${() => this.tab = 'link'}>Link</a>
          </div>

          <div class="form-control mb-3">
            <label class="label"><span class="label-text">Post Title</span></label>
            <input class="input input-bordered w-full" type="text" .value=${this.title} @input=${e => this.title = (e.target as HTMLInputElement).value} />
          </div>

          ${this.tab === 'media' ? html`
            <div class="form-control mb-3">
              <label class="label"><span class="label-text">Post Body</span></label>
              <textarea class="textarea textarea-bordered w-full" rows="4" .value=${this.body} @input=${e => this.body = (e.target as HTMLTextAreaElement).value} @paste=${e => this.handlePasteBody(e)}></textarea>
            </div>
            <div class="form-control mb-4">
              <label class="label"><span class="label-text">Upload Media</span></label>
              <input class="file-input file-input-accent w-full" type="file" multiple @change=${this.handleFiles} />
              <div class="flex flex-wrap mt-2 gap-2">
                ${this.files.map(file => html`<img src=${URL.createObjectURL(file)} class="w-24 h-24 object-cover rounded" />`)}
              </div>
            </div>
          ` : html`
            <div class="form-control mb-3">
              <label class="label"><span class="label-text">Link</span></label>
              <input class="input input-bordered w-full" type="text" .value=${this.link} @input=${e => this.handleLinkInput(e)} />
              ${this.renderPreview()}
            </div>
          `}

          ${this.error ? html`<div class="text-error mb-2">${unsafeHTML(this.error)}</div>` : ''}

          <button class="btn btn-primary w-full" ?disabled=${this.loading} @click=${this.handleSubmit}>Post</button>
          <form method="dialog" class="modal-backdrop">
            <button @click=${() => this.close()}>close</button>
          </form>
        </div>
      </dialog>
    `;
  }
}

